import React, {useState, useRef, FC} from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import CenterImage from "../../assets/icons/importPage.svg";
import { CustomButton } from "../../components/buttons/customButton";
import ImportModal from "../../components/importModal/ImportModal";
import ImportTableComponent from "../../components/importTable/ImportTable";
import SecondaryButton from "../../components/buttons/secondaryButton";
import {Colors} from "../../constants/constants";
import CustomDateRangePicker from "../../partials/customDateRangePicker/customDateRangePicker";
import PlusIcon from '../../assets/icons/plusImport.svg'
import {useDashboardFilterDateStore} from "../../store/dateRangeStore";
import useGetImports from "../../apiHooks/queries/getImports/useGetImports";
import {sendUploadFile} from "../../apiHooks/mutations/sendUpload/SendUpload";

const ImportAds = () => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const templateFileInputRef = useRef<HTMLInputElement | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [columns, setColumns] = useState<string[]>([]);
    const [previewData, setPreviewData] = useState<string[][]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [mappedColumns, setMappedColumns] = useState<{ [key: number]: string }>({});
    const [fileName, setFileName] = useState("");
    const [ isCalendarOpen, setIsCalendarOpen ] = useState<boolean>(false);
    const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);
    const setDashboardFilterDateRange = useDashboardFilterDateStore((state) => state.setDashboardFilterDateRange);
    const [ openModal , setOpenModal ] = useState<boolean>(false);

    const { data, refetch } = useGetImports(
        "GET_IMPORTS",
        dashboardFilterDateRange[0],
        dashboardFilterDateRange[1]
    )

    const { mutate: uploadFile } = sendUploadFile(() => refetch());

    const handleDownload = () => {
        const link = document.createElement("a");
        link.href = '/files/digger-template-2.xlsx';
        link.download = "digger-template.xlsx";
        link.click();
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const uploadedFile = e.target.files[0];
            setFile(uploadedFile);

            const reader = new FileReader();
            reader.onload = (evt) => {
                if (!evt.target?.result) return;
                const data = evt.target.result as ArrayBuffer;
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as string[][];

                if (jsonData.length > 0) {
                    setColumns(jsonData[0] as string[]);
                    setPreviewData(jsonData.slice(1));
                    setModalOpen(true);
                }
            };

            setOpenModal(false);

            if (uploadedFile.name.endsWith(".csv")) {
                reader.readAsText(uploadedFile);
            } else {
                reader.readAsArrayBuffer(uploadedFile);
            }
        }
    };

    const handleTemplateFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const uploadedFile = e.target.files[0];
            const formData = new FormData();
            formData.append("file", uploadedFile);
            formData.append("upload_type", "template");

            setOpenModal(false);

            uploadFile(formData);
        }
    };

    const closeModal =() => {
        setColumns([] as string[]);
        setPreviewData([]);
        setMappedColumns({});
        setModalOpen(false);
    }

    const triggerTemplateFileInput = () => {
        templateFileInputRef.current?.click();
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    if (data && data?.data?.data.length > 0 && !openModal) return (
        <div className="flex flex-col rounded-l justify-between pb-[14px] pt-[5px] items-center w-full bg-white text-primary">
            <div className="flex justify-end px-4 py-2 w-full gap-2">
                <SecondaryButton
                    type="button"
                    style={{
                        paddingInline: '1rem',
                        height: '40px',
                        marginLeft: '1rem',
                        paddingBlock: 0,
                        backgroundColor: 'white',
                        border: `1px solid ${isCalendarOpen ? Colors.primary : '#eaeaea'}`,
                    }}
                    onMouseDown={() => setIsCalendarOpen(true)}
                    dataCy="dashboard-actions-calendar-toggle"
                >
                    <CustomDateRangePicker
                        filterRange={dashboardFilterDateRange}
                        setFilterRange={setDashboardFilterDateRange}
                        isCalendarOpen={isCalendarOpen}
                        setIsCalendarOpen={setIsCalendarOpen}
                        iconButton
                        showFilterDates
                    />
                </SecondaryButton>
                <CustomButton onClick={() => setOpenModal(true)}  variant={"icon"} className="flex justify-center align-center h-[40px] w-[40px] text-white border-none">
                    <img src={PlusIcon} alt="" className="h-4 w-4"/>
                </CustomButton>
                {/*<input*/}
                {/*    type="file"*/}
                {/*    accept=".csv, .xlsx"*/}
                {/*    onChange={handleFileUpload}*/}
                {/*    ref={fileInputRef}*/}
                {/*    className="hidden"*/}
                {/*/>*/}
            </div>
        <ImportTableComponent data={data?.data?.data}/>
            {modalOpen && (
                <ImportModal
                    columns={columns}
                    previewData={previewData}
                    mappedColumns={mappedColumns}
                    setMappedColumns={setMappedColumns}
                    closeModal={() => closeModal()}
                    originalFileName={file?.name || "mapped_data"}
                />
            )}
        </div>
    )

   if(data && data?.data?.data.length === 0 || openModal) return (
        <div className="flex justify-center text-primary items-center w-full">
            <div className="flex flex-col h-[540px] rounded-l justify-between items-center w-full bg-white">
                <div className="flex text-base justify-center items-center h-[55px] border-b border-b-[#BDBDBD] w-full">
                    {t("importDataTable")}
                </div>
                <div className="h-full text-center max-w-[428px] flex flex-col items-center justify-start gap-6 py-[24px]">
                    <img src={CenterImage} alt="Import Icon" />
                    <p>{t("importDataTableParagraph")} <br/> <p onClick={() => handleDownload()} className="text-[#2684FF] underline hover:cursor-pointer">{t('downloadTemplateExample')}</p></p>
                    <div className="flex items-center justify-between gap-4">
                        <CustomButton onClick={() => triggerTemplateFileInput()}>{t('uploadOurTemplate')}</CustomButton>
                        <CustomButton variant="outline" onClick={triggerFileInput}>{t('mapYourTemplate')}</CustomButton>
                    </div>
                    <div className="flex items-center justify-center">
                        <CustomButton onClick={() => setOpenModal(false)} variant="noOutline">{t('cancel')}</CustomButton>
                    </div>
                    <input
                        type="file"
                        accept=".csv, .xlsx"
                        onChange={handleFileUpload}
                        ref={fileInputRef}
                        className="hidden"
                    />
                    <input
                        type="file"
                        accept=".csv, .xlsx"
                        onChange={handleTemplateFileUpload}
                        ref={templateFileInputRef}
                        className="hidden"
                    />
                </div>
            </div>
            {modalOpen && (
                <ImportModal
                    columns={columns}
                    previewData={previewData}
                    mappedColumns={mappedColumns}
                    setMappedColumns={setMappedColumns}
                    closeModal={() => closeModal()}
                    originalFileName={file?.name || "mapped_data"}
                />
            )}
        </div>
    );

   return <div></div>
};

export default ImportAds;
